import { Injectable, OnDestroy } from '@angular/core';
import { uniqueId } from 'lodash-es';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

export interface CacheItem<T> {
    Item: ReplaySubject<T>;
    Dirty: boolean;
    LastFetched: Date;
    BulkFetchCall?: string;
}

// Service name -> Index value -> Item
export type GlobalCache = Record<
    string,
    { BulkFetchCalls: Map<string, () => CacheItem<any>[]>; Items: Map<string, CacheItem<any>> }
>;

@Injectable({
    providedIn: 'root'
})
export class ShCachingService implements OnDestroy {
    private readonly CACHE_TIMEOUT: number = 1800000; // 30 minutes;

    private cache: GlobalCache = {};

    public register(serviceName: string) {
        this.cache[serviceName] = {
            Items: new Map(),
            BulkFetchCalls: new Map()
        };
    }

    /**
     * Fetch the item through its id
     * @param serviceName The name of the service the call originates from
     * @param index The index of the item (usually id)
     * @param callback The function to fetch the item from the backend. Id should be the first argument
     * @param args Arguments to pass to the callback function (you will need to repeat the id)
     * @returns
     */
    public getByIndex<T>(
        serviceName: string,
        index: string,
        callback: (...args: any[]) => Observable<T | undefined>,
        ...args: any[]
    ): Observable<T> {
        let cacheItem: CacheItem<T> | undefined = this.getCacheItemFromCache(serviceName, index);

        if (cacheItem) {
            return cacheItem.Item.pipe(take(1));
        }

        cacheItem = this.addCachePlaceHolder(serviceName, index);

        callback(...args)
            .pipe(
                take(1),
                catchError(() => {
                    console.error(`Failed to fetch ${index} for service ${serviceName}`);
                    return of(undefined);
                })
            )
            .subscribe(result => {
                const placeHolder: CacheItem<T> | undefined = this.cache[serviceName].Items.get(index);
                if (!placeHolder) {
                    console.error(`Something went wrong while adding ${index} to the cache.`);
                    return;
                }

                placeHolder.Item.next(result);
            });

        return cacheItem.Item.pipe(take(1));
    }

    /**
     * Fetch the item through unorthodox means (like a search fetch)
     * @param serviceName The name of the service the call originates from
     * @param identifier The main idenfifying property of the callback function (e.g. title when searching by title)
     * @param accessor A function to access the index on the item
     * @param indexAccessor A function to access the index on the item
     * @param callback The function to fetch the item from the backend.
     * @param args Arguments to pass to the callback function (you will need to repeat the index)
     * @returns
     */
    public getBy<T, S>(
        serviceName: string,
        identifier: S,
        accessor: (item: T) => S,
        indexAccessor: (item: T) => string,
        callback: (...args: any[]) => Observable<T | undefined>,
        ...args: any[]
    ): Observable<T | undefined> {
        const items: Observable<T>[] = Object.values(this.cache[serviceName].Items)
            .filter(
                cacheItem =>
                    !cacheItem.Dirty && new Date().getTime() - cacheItem.LastFetched.getTime() < this.CACHE_TIMEOUT
            )
            .map(cacheItem => cacheItem.Item); // Filter out items that are modified or out-of-date
        const mergedItems: Observable<T[]> = items.length > 0 ? combineLatest(items) : of([]);

        const fetchStream$: Observable<T | undefined> = callback(...args).pipe(
            take(1),
            switchMap(item => (item ? this.addCacheItem(serviceName, indexAccessor(item), item) : of(undefined)))
        );

        return mergedItems.pipe(
            map(items => items.find(item => identifier === accessor(item))),
            switchMap(item => (item ? of(item) : fetchStream$)),
            take(1) // Since we combine the entire cache, things could get funky
        );
    }

    /**
     * Fetch the items through a list fetch method. This method assumes the provided indexes are included in the result.
     * Any results not explicitly provided will not be cached.
     * The call used to fetch these items will be cached and re-executed when items become out-of-date (CACHE_TIMEOUT)
     * @param serviceName The name of the service the call originates from
     * @param indexes The indexes of the items (usually ids)
     * @param indexAccessor A function to access the index on the item
     * @param callback The function to fetch the items from the backend.
     * @param args Arguments to pass to the callback function (you will need to repeat the indexes)
     * @returns
     **/
    public getMultipleWithPlaceholders<T>(
        serviceName: string,
        indexes: string[],
        indexAccessor: (item: T) => string,
        callback: (...args: any[]) => Observable<T[]>,
        ...args: any[]
    ): Observable<T[]> {
        let cacheItems: CacheItem<T>[] = [];

        indexes.every(index => {
            // Abusing 'every' for the ability to break out of the loop
            let cacheItem = this.getCacheItemFromCache<T>(serviceName, index);

            if (!cacheItem) {
                // If one fails, stop looking in the cache and fetch all values instead
                cacheItems = this.addMultipleCacheItemsWithPlaceholder(
                    serviceName,
                    indexes,
                    indexAccessor,
                    callback,
                    args
                );
                return false;
            }

            cacheItems.push(cacheItem);
            return true;
        });

        if (cacheItems.length === 0) {
            return of([]);
        }

        return combineLatest(cacheItems.map(cacheItem => cacheItem.Item)).pipe(
            map(items => items.filter((item): item is T => !!item)),
            take(1)
        );
    }

    public markDirty<T>(serviceName: string, index: string): Observable<boolean> {
        const cacheItem: CacheItem<T> | undefined = this.cache[serviceName].Items.get(index);
        if (!cacheItem) {
            console.warn(`${index} not found in the ${serviceName} cache. Skipping update.`);
            return of(true);
        }

        cacheItem.Dirty = true;

        return of(true);
    }

    /**
     * Marks an item dirty if it is older than a given date
     * @param serviceName The service to be checked
     * @param index The index of the item
     * @param mustBeNewerThan The date to check against
     * @returns Whether the item was marked. False if the item was not stored in the cache
     */
    public markIfOutDated<T>(serviceName: string, index: string, mustBeNewerThan: Date): boolean {
        const cacheItem: CacheItem<T> | undefined = this.cache[serviceName].Items.get(index);
        if (!cacheItem) {
            return false;
        }

        if (cacheItem.LastFetched.getTime() < mustBeNewerThan.getTime()) {
            cacheItem.Dirty = true;
            return true;
        }

        return false;
    }

    public ngOnDestroy(): void {
        Object.values(this.cache).forEach(serviceCache =>
            Object.values(serviceCache.Items).forEach(cacheItem => cacheItem.Item.complete())
        );
    }

    // #region Util
    /**
     * @returns The cacheItem if it exists in the cache, is not dirty and is up-to-date; else undefined
     */
    private getCacheItemFromCache<T>(serviceName: string, index: string): CacheItem<T> | undefined {
        let cacheItem: CacheItem<T> | undefined = this.cache[serviceName]?.Items.get(index);

        if (cacheItem && !cacheItem.Dirty) {
            if (new Date().getTime() - cacheItem.LastFetched.getTime() < this.CACHE_TIMEOUT) {
                return cacheItem; // Item is neither dirty, nor outdated -> return cached value
            } else {
                // Item is out-of-date, refresh using bulk fetch if needed
                if (!!this.refreshBulkFetch(serviceName, cacheItem)) {
                    // Item has been refreshed, try again
                    cacheItem = this.cache[serviceName]?.Items.get(index);

                    // Check if refreshing was successful
                    if (cacheItem && new Date().getTime() - cacheItem.LastFetched.getTime() < this.CACHE_TIMEOUT) {
                        return cacheItem;
                    } else {
                        console.error(
                            `Something went wrong while refreshing ${index} for ${serviceName} using bulk fetch.`
                        );
                    }
                }
            }
        }

        return undefined;
    }

    /**
     * Creates a placeholder in the cache and immediately initialises it with the item
     * @returns The item
     */
    private addCacheItem<T>(serviceName: string, index: string, item: T): Observable<T> {
        const cacheItem: CacheItem<T> = this.addCachePlaceHolder(serviceName, index);
        cacheItem.Item.next(item);

        return cacheItem.Item;
    }

    /**
     * Adds a placeholder in the cache to ensure later calls don't start their own requests while the previous one is still processing
     * @returns The placeholder (as CacheItem)
     */
    private addCachePlaceHolder<T>(serviceName: string, index: string, bulkFetchId?: string): CacheItem<T> {
        const newCacheItem: CacheItem<T> = {
            Item: new ReplaySubject<T>(1),
            Dirty: false,
            LastFetched: new Date(),
            BulkFetchCall: bulkFetchId
        };

        const oldCacheItem: CacheItem<T> | undefined = this.cache[serviceName].Items.get(index);
        if (oldCacheItem) {
            oldCacheItem.Item.pipe(take(1)).subscribe(
                () => oldCacheItem.Item.complete() // Properly terminate old value, but only after it has emitted once (to prevent issues with overlaps)
            );
        }
        this.cache[serviceName].Items.set(index, newCacheItem);

        return newCacheItem;
    }

    /**
     * Creates placeholders for provided indexes and THEN fetches the items through the callback function
     * Any results not explicitly requested will not be cached.
     * @param serviceName The name of the service the call originates from
     * @param indexes The indexes of the items (usually ids)
     * @param indexAccessor A function to access the index on the item
     * @param callback The function to fetch the items from the backend.
     * @param args Arguments to pass to the callback function (you will need to repeat the indexes)
     * @returns A list of created cache items
     */
    private addMultipleCacheItemsWithPlaceholder<T>(
        serviceName: string,
        indexes: string[],
        indexAccessor: (item: T) => string,
        callback: (...args: any[]) => Observable<T[]>,
        args: any[]
    ): CacheItem<T>[] {
        // Cache the request to refresh later
        const callId: string = uniqueId();
        this.cache[serviceName].BulkFetchCalls.set(callId, () =>
            this.addMultipleCacheItemsWithPlaceholder(serviceName, indexes, indexAccessor, callback, args)
        );

        const cacheItems: CacheItem<T>[] = [];
        indexes.forEach(index => {
            cacheItems.push(this.addCachePlaceHolder(serviceName, index, callId));
        });

        callback(...args)
            .pipe(take(1))
            .subscribe(results => {
                if (results.length !== cacheItems.length) {
                    console.error(
                        `ShCachingService::getMultiple Expected results for ${serviceName} did not match actual result. Expected ${cacheItems.length}, got ${results.length}`
                    );
                }

                indexes.forEach((index, indexNumber) => {
                    // Iterate over indexes instead of results to ensure all cacheItems emit once
                    const resultItem: T | undefined = results.find(result => indexAccessor(result) === index);
                    if (!resultItem) {
                        console.error(
                            `ShCachingService::getMultiple Could not find ${index} in results for ${serviceName}`
                        );
                    }

                    cacheItems[indexNumber].Item.next(resultItem);
                });
            });

        return cacheItems;
    }
    // #endregion

    private refreshBulkFetch<T>(serviceName: string, cacheItem: CacheItem<T>): CacheItem<T>[] | undefined {
        if (cacheItem.BulkFetchCall) {
            const bulkFetchCall: (() => CacheItem<T>[]) | undefined = this.cache[serviceName].BulkFetchCalls.get(
                cacheItem.BulkFetchCall!
            );
            if (bulkFetchCall) {
                // Cleanup since a new Id will be generated
                this.cache[serviceName].BulkFetchCalls.delete(cacheItem.BulkFetchCall);
                return bulkFetchCall();
            }
        }

        return undefined;
    }
}
